import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm, type UseFormReturn } from "react-hook-form";
import { z } from "zod";

import { useAccessControl } from "../../app/use-access-control";
import type { CenterDetail } from "../../hooks/use-center";
import { useReferenceData } from "../../hooks/use-reference-data";
import { CenterMultiSelector } from "../forms/center-multi-selector";
import { isOptionalHourlyRateValid, isValidOptionalEmployeeNumber } from "../forms/form-helpers";
import { FormDividerRow, FormRow } from "../forms/form-row";
import { InputField } from "../forms/input-field";
import { CreateMailbox } from "../forms/legacy/create-mailbox";
import { SelectField } from "../forms/select-field";

export interface EditAccountInformationProps {
  editAccountForm: UseFormReturn<EditAccountFormValues>;
  availableCenters: CenterDetail[];
  hasMailbox: boolean;
  isCurrentStep: boolean;
}

export const EditAccountForm: React.FC<EditAccountInformationProps> = ({
  editAccountForm: {
    register,
    control,
    formState: { errors }
  },
  availableCenters,
  hasMailbox,
  isCurrentStep
}) => {
  const { hasFeatureAccess } = useAccessControl();
  const {
    mutableRoles,
    positions: [_centerOwner, ...positions]
  } = useReferenceData();
  const canUseEmployeeId = hasFeatureAccess?.("employee_id");

  if (!isCurrentStep) {
    return null;
  }

  return (
    <div className="form step-body">
      <FormRow>
        <SelectField
          isRequired
          label="System Access Level"
          validationState={errors.role ? "error" : undefined}
          {...register("role")}
        >
          <option value="">Select a System Access Level</option>
          {mutableRoles.map(role => (
            <option key={role.value} value={role.value}>
              {role.name}
            </option>
          ))}
        </SelectField>
      </FormRow>

      <FormRow>
        <SelectField
          isRequired
          label="Position"
          validationState={errors.position ? "error" : undefined}
          {...register("position")}
        >
          <option value="">Select a Position</option>
          {positions.map(({ value, name }) => (
            <option key={value} value={value}>
              {name}
            </option>
          ))}
        </SelectField>
        <InputField
          isRequired
          label="Start Date"
          type="date"
          validationState={errors.startDate ? "error" : undefined}
          {...register("startDate")}
        />
      </FormRow>

      <FormRow>
        <InputField
          label="Hourly Rate"
          type="number"
          step="0.01"
          min="0"
          placeholder="Hourly Rate"
          validationState={errors.hourlyRate ? "error" : undefined}
          {...register("hourlyRate")}
        />
        {canUseEmployeeId ? (
          <InputField
            label="HR ID"
            placeholder="HR ID"
            inputMode="numeric"
            maxLength={15}
            validationState={errors.employeeNumber ? "error" : undefined}
            {...register("employeeNumber")}
          />
        ) : null}
      </FormRow>

      <FormRow>
        <Controller
          name="createMailbox"
          control={control}
          render={({ field: { value, onChange } }) => (
            <CreateMailbox value={value} disabled={hasMailbox} onChangeValue={onChange} />
          )}
        />
      </FormRow>

      <FormDividerRow />

      <p>This user will be added to the checked centers</p>

      <Controller
        name="centers"
        control={control}
        render={({ field: { value, onChange } }) => (
          <CenterMultiSelector checkedCenters={value} availableCenters={availableCenters} onChangeValue={onChange} />
        )}
      />
    </div>
  );
};

const editAccountFormSchema = z.object({
  createMailbox: z.boolean(),
  role: z.string().min(1),
  position: z.string().min(1),
  startDate: z.string().min(1),
  hourlyRate: z.string().refine(isOptionalHourlyRateValid, { message: "Must be a non-negative number" }),
  employeeNumber: z
    .string()
    .trim()
    .refine(isValidOptionalEmployeeNumber, { message: "Must be up to 15 digits" })
    .transform(it => it || null),
  centers: z.array(z.string())
});

type EditAccountFormValues = z.infer<typeof editAccountFormSchema>;

export const useEditAccountForm = (hasMailbox: boolean) => {
  const { activeCenterId } = useAccessControl();

  return useForm<EditAccountFormValues>({
    defaultValues: {
      createMailbox: hasMailbox,
      role: "",
      position: "",
      startDate: "",
      hourlyRate: "",
      employeeNumber: "",
      centers: [activeCenterId]
    },
    resolver: zodResolver(editAccountFormSchema),
    mode: "onTouched"
  });
};
